import type { Contact, ContactEvaluation, Attribution } from './contact';

export type { Attribution };

export interface SignalContact {
  id: string;
  persona_id: string;
  persona_short_name: string;
  first_name: string;
  last_name: string;
  photo_url: string;
  linkedin: string;
  qualification_reason?: string;
}

export type SignalEntity = 'linkedin' | 'email' | 'form' | 'page' | 'response' | 'icp' | 'persona';

export const WEBSITE_SIGNAL_TRIGGERS = ['visited_page', 'filled_form', 'abandoned_form'] as const;
export type WebsiteSignalTrigger = (typeof WEBSITE_SIGNAL_TRIGGERS)[number];

export const MARKET_SIGNAL_TRIGGERS = [
  'researched_topic',
  'explored_product',
  'evaluated_competitor',
] as const;
export type MarketSignalTrigger = (typeof MARKET_SIGNAL_TRIGGERS)[number];

export const LINKEDIN_SIGNAL_TRIGGERS = [
  'followed_page',
  'followed_competitor',
  'commented_post',
  'reacted_post',
  'authored_post',
] as const;
export type LinkedInSignalTrigger = (typeof LINKEDIN_SIGNAL_TRIGGERS)[number];

export type SignalType = 'website' | 'market' | 'linkedin';

export type SignalAction =
  | WebsiteSignalTrigger
  | MarketSignalTrigger
  | LinkedInSignalTrigger
  | 'connected'
  | 'opened'
  | 'received'
  | 'evaluated';

export interface SignalData {
  id: string;
  client_id: string;
  company_id: string;
  company_name?: string;
  company_linkedin?: string;
  icp_id: string;
  icp_short_name: string;
  qualification_reason?: string;
  action: SignalAction | string;
  trigger?: string;
  linkedin_trigger?: LinkedInSignalTrigger | string;
  reaction_type?: string;
  entity: SignalEntity | string;
  signal_at: string;
  contact: SignalContact | null;
  message:
    | string
    | {
        id: string;
        sender: {
          id: string;
          name: string;
          email: string;
          profile_pic_url: string;
        };
        title: string;
        body: string;
      }
    | null;
  page: {
    url: string;
    time_spent?: number;
    target_page_id?: string;
    target_page_short_name?: string;
  } | null;
  market_signal?: {
    topic: string;
  } | null;
  linkedin_signal?: {
    trigger?: LinkedInSignalTrigger | string;
    source: string;
    url: string;
    text: string;
    sentiment?: string;
    reaction_type?: string;
    is_relevant?: boolean;
    reason?: string;
    company_name?: string;
    post_title?: string;
  } | null;
  provider?: string;
  provider_id?: string;
  provider_type?: string;
  created_at: string;
  updated_at: string;
}

export interface SignalSocial {
  website: string;
  website_rank: number;
  facebook: string;
  twitter: string;
  linkedin: string;
  linkedin_followers: number;
  crunchbase: string;
}

export interface SignalCompany {
  id: string;
  client_id: string;
  icp_id: string;
  icp_short_name: string;
  qualification_reason: string;
  name: {
    short: string;
    full: string;
  };
  description: {
    short: string;
    long: string;
    details: {
      employee_count: number;
      annual_revenue: string;
      founded: string;
      [key: string]: any;
    };
  };
  logo: string;
  social: SignalSocial;
  signal: SignalData;
  total_signals: number;
  created_at: string;
  updated_at: string;
}

export interface SignalContactSummary {
  id: string;
  client_id: string;
  company_id: string;
  persona_id: string;
  qualification_reason?: string;
  contact_qualification_reason?: string;
  company_qualification_reason?: string;
  eval?: ContactEvaluation;
  attribution?: Attribution;
  first_name: string;
  last_name: string;
  title: string;
  photo_url: string;
  linkedin: string;
  company_name: string;
  company_logo: string;
  persona_name: string;
  icp_name: string;
  signal: SignalData;
  total_signals: number;
  status: string;
  created_at: string;
  updated_at: string;
}
export interface SignalGroup {
  contact_id: string;
  contact_name: string;
  company_id: string;
  company_name: string;
  type: SignalAction | string;
  entity: SignalEntity | string;
  total: number;
  signals: SignalData[];
}

export interface SignalDateGroup {
  date: string;
  total: number;
  groups?: SignalGroup[];
  target_pages?: SignalTargetPageGroup[];
}

export interface SignalTargetPageGroup {
  target_page_id: string;
  target_page_short_name: string;
  total: number;
  total_time_spent: number;
  groups: SignalGroup[];
}

export interface WebsiteSignalTargetPage {
  id: string;
  client_id: string;
  page_url: string;
  match_type: string;
  page_name: string;
  short_name: string;
  description: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface ContactSignalEventBase {
  id: string;
  contact_id: string;
  type: string;
  action?: SignalAction | string;
  trigger?: string;
  linkedin_trigger?: LinkedInSignalTrigger | string;
  market_trigger?: MarketSignalTrigger | string;
  company_name?: string;
  company_linkedin?: string;
  signal_at?: string;
  created_at: string;
  updated_at: string;
}

export interface ContactSignalSummary<TEvent extends ContactSignalEventBase> {
  total_count: number;
  last_signal: TEvent;
}

// List cards reuse the contact model; they do not need a second copy of its schema.
type SignalListContact = Pick<
  Contact,
  | 'id'
  | 'client_id'
  | 'contact_info'
  | 'company_info'
  | 'eval'
  | 'status'
  | 'attribution'
  | 'created_at'
  | 'updated_at'
>;

type SignalListSummary<TLastSignal> = {
  total_count: number;
  last_signal: TLastSignal;
};

export interface WebsiteSignalPayload {
  trigger: WebsiteSignalTrigger | string;
  url: {
    raw: string;
    canonical: string;
    domain: string;
    path: string;
  };
  attribution?: Attribution;
  utm?: {
    source?: string;
    medium?: string;
    campaign?: string;
    term?: string;
    content?: string;
  };
  target_page: {
    id: string;
    name: string;
    short_name: string;
    description: string;
  } | null;
  session: {
    id: string;
    started_at: string;
    ended_at: string;
    duration_secs: number;
  };
  page?: {
    visited_at?: string;
    time_spent_secs?: number;
    visit_count?: number;
    first_visited_at?: string;
    last_visited_at?: string;
    total_time_spent_secs?: number;
  };
}

export interface WebsiteSignalEvent extends ContactSignalEventBase {
  type: 'website' | string;
  captured_at?: string;
  website: WebsiteSignalPayload;
}

export interface WebsiteSignalDateGroup {
  date: string;
  total: number;
  signals: WebsiteSignalEvent[];
}

export interface WebsiteSignalSummary extends ContactSignalSummary<WebsiteSignalEvent> {
  total_duration_secs: number;
}

export interface WebsiteSignalItemData {
  contact: SignalListContact;
  website_signal_summary: SignalListSummary<{
    id: string;
    type: string;
    captured_at?: string;
    created_at: string;
    updated_at: string;
    website: {
      trigger: string;
      url: { raw: string };
      session: { duration_secs: number };
      target_page: { id: string; name: string; short_name: string } | null;
      attribution?: Attribution;
    };
  }>;
}

export interface MarketSignalEvent extends ContactSignalEventBase {
  type: 'market' | string;
  action?: MarketSignalTrigger | string;
  captured_at?: string;
  provider?: string;
  attribution?: Attribution;
  market: {
    trigger?: string;
    topic: string;
    company_website?: string;
    provider?: string;
    attribution?: Attribution;
  };
}

export type MarketSignalSummary = ContactSignalSummary<MarketSignalEvent>;

export interface MarketSignalDateGroup {
  date: string;
  total: number;
  signals: MarketSignalEvent[];
}

export interface MarketSignalItemData {
  contact: SignalListContact;
  market_signal_summary: SignalListSummary<{
    id: string;
    type: string;
    action?: string;
    signal_at?: string;
    captured_at?: string;
    provider?: string;
    created_at: string;
    updated_at: string;
    attribution?: Attribution;
    market: {
      trigger?: MarketSignalTrigger | string;
      topic: string;
      provider?: string;
      attribution?: Attribution;
    };
  }>;
}

export interface LinkedInSignalEvent extends ContactSignalEventBase {
  type: 'linkedin' | string;
  trigger?: LinkedInSignalTrigger | string;
  linkedin_trigger?: LinkedInSignalTrigger | string;
  action?: LinkedInSignalTrigger | string;
  captured_at?: string;
  attribution?: Attribution;
  linkedin_signal?: {
    trigger?: LinkedInSignalTrigger | string;
    linkedin_trigger?: LinkedInSignalTrigger | string;
    source: string;
    url: string;
    text: string;
    sentiment?: string;
    reaction_type?: string;
    is_relevant?: boolean;
    reason?: string;
    attribution?: Attribution;
    company_name?: string;
    post_title?: string;
  };
  linkedin?: {
    trigger?: LinkedInSignalTrigger | string;
    linkedin_trigger?: LinkedInSignalTrigger | string;
    source: string;
    url: string;
    text: string;
    sentiment?: string;
    reaction_type?: string;
    is_relevant?: boolean;
    reason?: string;
    attribution?: Attribution;
    company_name?: string;
    post_title?: string;
  };
}

export type LinkedInSignalSummary = ContactSignalSummary<LinkedInSignalEvent>;

export interface LinkedInSignalDateGroup {
  date: string;
  total: number;
  signals: LinkedInSignalEvent[];
}

export interface LinkedInSignalItemData {
  contact: SignalListContact;
  linkedin_signal_summary: SignalListSummary<{
    id: string;
    type: string;
    trigger?: string;
    linkedin_trigger?: LinkedInSignalTrigger | string;
    signal_at?: string;
    captured_at?: string;
    created_at: string;
    updated_at: string;
    attribution?: Attribution;
    linkedin_signal?: NonNullable<LinkedInSignalEvent['linkedin_signal']>;
  } | null>;
}
